import { get } from 'lodash';
import { acceptedMediaTypes } from 'src/constants';
import {
  type ApiResponse,
  type CCApiResponse,
  type ICCAsset,
  IAsset,
  IHQAsset,
} from 'src/interfaces';
import { getPreviewUrlSrc, getThumbnailSrc } from 'src/utils/gcf-helper';

// blob URL means a local placeholder.
export const isPlaceholderUrl = (url?: string): boolean =>
  !!url?.startsWith('blob:');

export const mapCCAssets = (response: CCApiResponse, sbAssets?: IAsset[]) => {
  return response.data.map((asset: ICCAsset) =>
    mapCCAsset(asset, response.includes, sbAssets),
  );
};

const mapCCAsset = (
  data: ICCAsset,
  includes?: ApiResponse,
  sbAssets?: IAsset[],
) => {
  const matchedSBAsset = sbAssets?.find(
    (sbAsset) => sbAsset.fileName === data.name,
  );

  // Use the SB asset's URL only when it's a real (non-blob) URL. For a
  // placeholder (blob) — or a missing SB match (e.g. a post-upload refetch
  // replaced the placeholder before the backend indexed the asset) — build the
  // real backend URL from the name, so detail/preview pages always have a
  // working URL and never go empty.
  const sbPreviewUrl = matchedSBAsset?.previewUrl;
  const sbThumbnailUrl = matchedSBAsset?.thumbnailUrl;

  return {
    ...data,
    previewUrl:
      sbPreviewUrl && !isPlaceholderUrl(sbPreviewUrl)
        ? sbPreviewUrl
        : getPreviewUrlSrc(data.name),
    thumbnailUrl:
      sbThumbnailUrl && !isPlaceholderUrl(sbThumbnailUrl)
        ? sbThumbnailUrl
        : getThumbnailSrc(data.name),
    height: matchedSBAsset?.height,
    width: matchedSBAsset?.width,
    themes: get(includes, ['themes', 'data'], []),
  };
};

const uploadCacheBusters: Record<string, number> = {};

export const setUploadCacheBuster = (fileName: string, timestamp: number) => {
  uploadCacheBusters[fileName] = timestamp;
};

export const mapAllAssets = (assets: IHQAsset[]): IAsset[] => {
  return filterMediaAssets(assets).map((asset: IHQAsset) => {
    return mapAsset(asset);
  });
};

const mapAsset = (asset: IHQAsset): IAsset => {
  const name = get(asset, 'name', '');
  const modifiedAt = get(asset, 'modified_at', '');
  const baseThumbnailUrl = getThumbnailSrc(name);
  const serverTimestamp = modifiedAt ? new Date(modifiedAt).getTime() : 0;
  const cacheBuster = Math.max(serverTimestamp, uploadCacheBusters[name] ?? 0);

  return {
    fileName: name,
    fileSize: Number(get(asset, 'file_size', 0)),
    assetName: name,
    assetId: get(asset, 'id', '').toString(),
    width: get(asset, 'width', 0),
    height: get(asset, 'height', 0),
    uploaded: get(asset, 'uploaded', ''),
    previewUrl: cacheBuster
      ? `${getPreviewUrlSrc(name)}?t=${cacheBuster}`
      : getPreviewUrlSrc(name),
    thumbnailUrl: cacheBuster
      ? `${baseThumbnailUrl}?t=${cacheBuster}`
      : baseThumbnailUrl,
  };
};

const filterMediaAssets = (assets: IHQAsset[]) => {
  return assets.filter((asset) =>
    acceptedMediaTypes.find((type) => get(asset, 'name', '').includes(type)),
  );
};

// Display-only asset so an upload shows in the grid instantly (local blob URL)
// before the backend indexes it. The blob is revoked on image-selector unmount.
export const buildPlaceholderAsset = (file: File): IAsset => {
  const objectUrl = URL.createObjectURL(file);

  return {
    fileName: file.name,
    fileSize: file.size,
    assetName: file.name,
    assetId: file.name,
    width: 0,
    height: 0,
    uploaded: new Date().toISOString(),
    previewUrl: objectUrl,
    thumbnailUrl: objectUrl,
  };
};

export const sortAssetsByUploadDate = (assets: IAsset[]): IAsset[] => {
  return assets.sort(
    (a, b) => new Date(b.uploaded).getTime() - new Date(a.uploaded).getTime(),
  );
};

//Convert hex to base64
export const hexToBase64 = (hex: string) => {
  const bytes = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substr(i, 2), 16));
  }

  return btoa(String.fromCharCode(...bytes));
};
